import { boolean, object, string } from 'yup';
import type { UseFormSetValue } from 'react-hook-form';
import type { Staff } from '~/models/Staff';
import type { CompanyVendor } from './CompanyVendor';
import type { Subtype } from '~/models/Subtype';
import type { Property, PropertyType } from '~/models/kensaku_kun/property';
import { buildPropertyByDeal } from '~/models/kensaku_kun/property';
import type { DealFormRepairParam } from './DealRepair';
import type { Deal, DealType, InspectionBy } from '~/models/Deal';
import type { PropertyDealBuilding, PropertyDealRoom } from './PropertyDeal';
import type { PropertyRoomOption } from '~/models/PropertyRoomOption';

export type DealParam = {
  dealType: 'restoration' | 'repair' | 'other' | null;
  dealSubtype: Subtype | null; // idのみで良いかもしれない
  roomName: string | null;
  roomNumber: string | null;
  address: string | null;
  enableAutoLock: 'true' | 'false' | null;
  enableElevator: 'true' | 'false' | null;
  conductFieldSurvey: 'true' | 'false' | null;
  placeKeyStoring: string;
  freeParking: string;
  floorMap: File | null;
  leaseContractFile1: File | null;
  leaseContractFile2: File | null;
  leaseContractFile3: File | null;
  dealRepair: DealFormRepairParam | null;
  tenantName: string | null;
  tenantNameKana: string | null;
  tenantTel: string | null;
  tenantEmail: string | null;
  terminationDate: string | null;
  inspectionBy: InspectionBy;
  inspectionCandidateDate1: string | null;
  inspectionCandidateDateTime1: string | null;
  inspectionCandidateDate2: string | null;
  inspectionCandidateDateTime2: string | null;
  inspectionCandidateDate3: string | null;
  inspectionCandidateDateTime3: string | null;
  staff: Staff | null; // idのみで良いかもしれない
  companyVendor: CompanyVendor | null; // idのみで良いかもしれない
  showTenant: boolean;
  estimationByCompany: boolean;
  estimateAddressName: string;
  note: string;
  property: Property<PropertyType> | null;
  preferredDate: string | null;
  isRegisterAsBuilding: boolean;
  amaterasBuildingPropertyId: string | null;
  leasingId: string | null;
};

export type RelatedDealParam = Omit<
  DealParam,
  | 'property'
  | 'isRegisterAsBuilding'
  | 'roomName'
  | 'roomNumber'
  | 'address'
  | 'enableAutoLock'
  | 'enableElevator'
  | 'placeKeyStoring'
  | 'freeParking'
  | 'floorMap'
  | 'leasingId'
  | 'preferredDate'
  | 'leaseContractFile1'
  | 'leaseContractFile2'
  | 'leaseContractFile3'
  | 'amaterasBuildingPropertyId'
>;

export const buildDealParam = ({
  inspectionBy,
  dealType,
  leasingId
}: {
  inspectionBy: InspectionBy;
  dealType: DealType | null;
  leasingId: string | null;
}): DealParam => ({
  dealType,
  dealSubtype: null,
  roomName: '',
  roomNumber: '',
  address: '',
  enableAutoLock: null,
  enableElevator: null,
  conductFieldSurvey: null,
  placeKeyStoring: '',
  freeParking: '',
  floorMap: null,
  leaseContractFile1: null,
  leaseContractFile2: null,
  leaseContractFile3: null,
  dealRepair: {
    requestContent: '',
    itemName: '',
    itemCategory: '',
    manufacturer: '',
    modelNumber: '',
    modelNumberFile: null
  },
  tenantName: '',
  tenantNameKana: '',
  tenantTel: '',
  tenantEmail: '',
  terminationDate: null,
  inspectionBy,
  inspectionCandidateDate1: null,
  inspectionCandidateDateTime1: '',
  inspectionCandidateDate2: null,
  inspectionCandidateDateTime2: '',
  inspectionCandidateDate3: null,
  inspectionCandidateDateTime3: '',
  staff: null,
  companyVendor: null,
  showTenant: true,
  estimationByCompany: false,
  estimateAddressName: '',
  note: '',
  property: null,
  preferredDate: null,
  isRegisterAsBuilding: false,
  amaterasBuildingPropertyId: null,
  leasingId
});

export type DealUpdateParam = Omit<
  DealParam,
  | 'dealSubtype'
  | 'floorMap'
  | 'leaseContractFile1'
  | 'leaseContractFile2'
  | 'leaseContractFile3'
  | 'dealRepair'
  | 'conductFieldSurvey'
  | 'inspectionCandidateDate1'
  | 'inspectionCandidateDateTime1'
  | 'inspectionCandidateDate2'
  | 'inspectionCandidateDateTime2'
  | 'inspectionCandidateDate3'
  | 'inspectionCandidateDateTime3'
  | 'companyVendor'
  | 'showTenant'
  | 'estimationByCompany'
  | 'leasingId'
> & {
  name: string | null;
  amaterasPropertyId: number | null;
};

export const buildDealUpdateParam = (deal: Deal): DealUpdateParam => ({
  dealType: deal.dealType,
  amaterasPropertyId: deal.propertyId,
  property: buildPropertyByDeal(deal),
  name: deal.name,
  roomName: deal.name,
  roomNumber: deal.roomNumber,
  address: deal.address,
  enableAutoLock: deal.enableAutoLock ? 'true' : 'false',
  enableElevator: deal.enableElevator ? 'true' : 'false',
  placeKeyStoring: deal.placeKeyStoring,
  freeParking: deal.freeParking,
  inspectionBy: deal.inspectionBy,
  tenantName: deal.tenantName,
  tenantNameKana: deal.tenantNameKana,
  tenantTel: deal.tenantTel,
  tenantEmail: deal.tenantEmail,
  terminationDate: deal.terminationDate,
  staff: deal.staff,
  estimateAddressName: deal.estimateAddressName,
  note: deal.note,
  preferredDate: deal.preferredDate,
  isRegisterAsBuilding: deal.isRegisterAsBuilding,
  amaterasBuildingPropertyId: deal.buildingPropertyId
    ? String(deal.buildingPropertyId)
    : null
});

export const resetBuildingValues = (
  setValue: UseFormSetValue<DealParam | DealUpdateParam>
): void => {
  setValue('placeKeyStoring', '');
  setValue('freeParking', '');
  setValue('enableElevator', null);
  setValue('enableAutoLock', null);
};
export const resetTenantValues = (
  setValue: UseFormSetValue<DealParam>
): void => {
  setValue('tenantName', '');
  setValue('tenantNameKana', '');
  setValue('tenantTel', '');
  setValue('tenantEmail', '');
};

export const setBuildingValues = (
  setValue: UseFormSetValue<DealParam | DealUpdateParam>,
  propertyDeal: PropertyDealRoom | PropertyDealBuilding
): void => {
  setValue('placeKeyStoring', propertyDeal.placeKeyStoring);
  setValue('freeParking', propertyDeal.freeParking);
  setValue('enableElevator', propertyDeal.enableElevator ? 'true' : 'false');
  setValue('enableAutoLock', propertyDeal.enableAutoLock ? 'true' : 'false');
};

export const setTenantValues = (
  setValue: UseFormSetValue<DealParam>,
  propertyRoomOption: PropertyDealRoom | PropertyRoomOption // TODO: NSKDEV-1866でDealEditorBodyV2に切り替え後PropertyDealRoomは消す
): void => {
  setValue('tenantName', propertyRoomOption.tenantName ?? '');
  setValue('tenantNameKana', propertyRoomOption.tenantNameKana ?? '');
  setValue('tenantTel', propertyRoomOption.tenantTel ?? '');
  setValue('tenantEmail', propertyRoomOption.tenantEmail ?? '');
};

export const requestContentPlaceholder =
  '例）\nいつから （日付）:5/30（入居時）\nどこで （場所）：キッチン\n何を（物） ：タオル掛け\nなぜ （経緯）：入居時から壊れてた\nどのように（状況）:接着剤で止めた跡があるが、片方に亀裂が入りすでに割れてる。もう一方でずっと支えてる形になってるのでいつ壊れてもおかしくない';

export const validationSchemaV2 = object().shape({
  dealType: string().nullable().required('入力してください'),
  dealSubtype: object()
    .nullable()
    .when('dealType', {
      is: 'other',
      then: (schema) => schema.required('入力してください')
    }),
  roomName: string().required('入力してください'),
  isRegisterAsBuilding: boolean().required('入力してください'),
  roomNumber: string().when('isRegisterAsBuilding', {
    is: false,
    then: (schema) => schema.required('入力してください')
  }),
  address: string().required('入力してください'),
  enableAutoLock: boolean().nullable().required('入力してください'),
  enableElevator: boolean().nullable().required('入力してください'),
  conductFieldSurvey: boolean()
    .nullable()
    .when('dealType', {
      is: 'repair',
      then: (schema) => schema.required('入力してください')
    }),
  dealRepair: object()
    .nullable()
    .when('dealType', {
      is: 'repair',
      then: (schema) =>
        schema.shape({
          requestContent: string().required('入力してください').nullable(),
          itemCategory: string().required('入力してください').nullable()
        })
    }),
  inspectionBy: string().required('入力してください'),
  tenantName: string().when('inspectionBy', {
    is: 'vendor',
    then: (schema) => schema.required('入力してください')
  }),
  tenantNameKana: string().when('inspectionBy', {
    is: 'vendor',
    then: (schema) => schema.required('入力してください')
  }),
  tenantTel: string()
    .when('inspectionBy', {
      is: 'vendor',
      then: (schema) => schema.required('入力してください')
    })
    .nullable()
    .matches(/^([-\d]*)?$/, {
      message: '電話番号は数字とハイフンのみで入力してください'
    })
    .matches(/^(0\d+-?\d+-?\d+)?$/, {
      message: '電話番号の形式を確認してください'
    })
    .test(
      'PhoneNumber',
      '電話番号は10桁または11桁で入力してください(-は数えません)',
      (val: string | undefined | null) =>
        [0, 10, 11].includes((val ?? '').replace(/-/g, '').length)
    ),
  tenantEmail: string()
    .nullable()
    .email('メールアドレスの形式を確認してください'),
  staff: object().nullable().required('入力してください'),
  companyVendor: object().nullable().required('入力してください'),
  estimateAddressName: string().required('入力してください')
});

export const updateValidationSchemaV2 = object().shape({
  roomName: string().required('入力してください'),
  isRegisterAsBuilding: boolean().required('入力してください'),
  roomNumber: string().when('isRegisterAsBuilding', {
    is: false,
    then: (schema) => schema.required('入力してください')
  }),
  address: string().required('入力してください'),
  enableAutoLock: boolean().nullable().required('入力してください'),
  enableElevator: boolean().nullable().required('入力してください'),
  inspectionBy: string().required('入力してください'),
  tenantName: string().when('inspectionBy', {
    is: 'vendor',
    then: (schema) => schema.required('入力してください')
  }),
  tenantNameKana: string().when('inspectionBy', {
    is: 'vendor',
    then: (schema) => schema.required('入力してください')
  }),
  tenantTel: string()
    .when('inspectionBy', {
      is: 'vendor',
      then: (schema) => schema.required('入力してください')
    })
    .nullable()
    .matches(/^([-\d]*)?$/, {
      message: '電話番号は数字とハイフンのみで入力してください'
    })
    .matches(/^(0\d+-?\d+-?\d+)?$/, {
      message: '電話番号の形式を確認してください'
    })
    .test(
      'PhoneNumber',
      '電話番号は10桁または11桁で入力してください(-は数えません)',
      (val: string | undefined | null) =>
        [0, 10, 11].includes((val ?? '').replace(/-/g, '').length)
    ),
  tenantEmail: string()
    .nullable()
    .email('メールアドレスの形式を確認してください'),
  staff: object().nullable().required('入力してください'),
  estimateAddressName: string().required('入力してください')
});

export const relatedDealValidationSchema = validationSchemaV2.omit([
  'isRegisterAsBuilding',
  'roomName',
  'roomNumber',
  'address',
  'enableAutoLock',
  'enableElevator'
]);
